/**
 * DepositUsdtSettlementsPage.js
 *
 * Pantalla CEO ↔ Proveedor CU: cuando el CEO confirma un depósito gestor,
 * acá aparece un stub "pending_provider_input" donde el proveedor declara
 * cuántos USDT recibió y la tasa UYU/USDT.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { Wallet, CheckCircle2, Clock, RefreshCw, Pencil, TrendingUp } from 'lucide-react';
import { extractErrorMessage } from '@/utils/errors';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending_provider_input: { tone: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Pendiente USDT' },
  completed:              { tone: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2, label: 'Completado' },
};

const fmt = (n, dec = 2) =>
  n == null ? '—' : Number(n).toLocaleString('es-ES', { minimumFractionDigits: dec, maximumFractionDigits: dec });

const DepositUsdtSettlementsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ usdt_received: '', rate_uyu_usdt: '', note: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/deposit-settlements`, { headers: getAuthHeaders() }),
        axios.get(`${API}/deposit-settlements/stats/monthly`, { headers: getAuthHeaders() })
          .catch(() => ({ data: { months: [] } })),
      ]);
      setItems(r1.data.items || []);
      setStats(r2.data?.months || []);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo cargar la lista'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return items;
    return items.filter((it) => it.status === statusFilter);
  }, [items, statusFilter]);

  const counts = useMemo(() => ({
    all: items.length,
    pending_provider_input: items.filter((i) => i.status === 'pending_provider_input').length,
    completed: items.filter((i) => i.status === 'completed').length,
  }), [items]);

  const openEdit = (s) => {
    setTarget(s);
    setForm({
      usdt_received: s.usdt_received ?? '',
      rate_uyu_usdt: s.rate_uyu_usdt ?? (s.amount_uyu ? (s.amount_uyu / 1).toFixed(4) : ''),
      note: s.note ?? '',
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!target) return;
    const usdt = parseFloat(form.usdt_received);
    if (!usdt || usdt <= 0) {
      toast.error('USDT recibido debe ser > 0');
      return;
    }
    setSaving(true);
    try {
      const body = { usdt_received: usdt };
      if (form.rate_uyu_usdt) body.rate_uyu_usdt = parseFloat(form.rate_uyu_usdt);
      if (form.note) body.note = form.note;
      await axios.put(`${API}/deposit-settlements/${target.id}`, body, { headers: getAuthHeaders() });
      toast.success('Cambio UYU↔USDT registrado');
      setTarget(null);
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo guardar'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="deposit-settlements-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Wallet className="w-6 h-6 text-blue-500" /> Recargas UYU → USDT
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Cuando el CEO confirma un depósito de gestor, registrá acá cuánto USDT recibiste y la tasa.
          </p>
        </div>
        <Button variant="ghost" onClick={load} data-testid="refresh-settlements">
          <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        {/* Chart + filters */}
        {stats.length > 0 && (
          <div className="p-4 border-b">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-blue-500" />
              <h3 className="text-sm font-semibold text-slate-700">Tasa promedio UYU / USDT por mes</h3>
            </div>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats} margin={{ top: 6, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} domain={['auto', 'auto']} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }}
                           formatter={(v) => Number(v).toFixed(4)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="avg_rate" name="Promedio" stroke="#2563eb" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="min_rate" name="Mínimo"   stroke="#10b981" strokeWidth={1} strokeDasharray="4 4" dot={false} />
                  <Line type="monotone" dataKey="max_rate" name="Máximo"   stroke="#ef4444" strokeWidth={1} strokeDasharray="4 4" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 p-3 border-b">
          {[
            { k: 'all', label: 'Todas' },
            { k: 'pending_provider_input', label: 'Pendientes USDT' },
            { k: 'completed', label: 'Completadas' },
          ].map((opt) => (
            <Button
              key={opt.k}
              size="sm"
              variant={statusFilter === opt.k ? 'default' : 'outline'}
              onClick={() => setStatusFilter(opt.k)}
              className={statusFilter === opt.k ? 'bg-blue-600 hover:bg-blue-700' : ''}
              data-testid={`filter-${opt.k}`}
            >
              {opt.label} <Badge variant="outline" className="ml-2 bg-white/10 border-0 text-[10px]">{counts[opt.k] || 0}</Badge>
            </Button>
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="p-3 text-left">Fecha</th>
                <th className="p-3 text-left">Gestor</th>
                <th className="p-3 text-right">UYU Recibido</th>
                <th className="p-3 text-right">USDT</th>
                <th className="p-3 text-right">Tasa UYU/USDT</th>
                <th className="p-3 text-left">Estado</th>
                <th className="p-3 text-right">Acción</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-6 text-center text-slate-400">Cargando…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="p-6 text-center text-slate-400">Sin recargas con este filtro.</td></tr>
              ) : filtered.map((s) => {
                const meta = STATUS_LABEL[s.status] || { tone: 'bg-slate-100 text-slate-600', icon: Clock, label: s.status };
                const Icon = meta.icon;
                return (
                  <tr key={s.id} className="border-t hover:bg-slate-50" data-testid={`settle-row-${s.id}`}>
                    <td className="p-3 text-xs text-slate-500">
                      {s.created_at ? new Date(s.created_at).toLocaleString('es-ES') : '—'}
                    </td>
                    <td className="p-3">{s.gestor_name || '—'}</td>
                    <td className="p-3 text-right font-mono font-semibold">
                      {fmt(s.amount_uyu)} <span className="text-[10px] text-slate-400">{s.currency}</span>
                    </td>
                    <td className="p-3 text-right font-mono">{fmt(s.usdt_received)}</td>
                    <td className="p-3 text-right font-mono text-xs">{fmt(s.rate_uyu_usdt, 4)}</td>
                    <td className="p-3">
                      <Badge className={`${meta.tone} font-medium`}><Icon className="w-3 h-3 mr-1 inline" />{meta.label}</Badge>
                    </td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-settle-${s.id.slice(0,8)}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Editor dialog */}
      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Registrar cambio UYU → USDT</DialogTitle>
          </DialogHeader>
          {target && (
            <form onSubmit={submit} className="space-y-3">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="text-slate-500 text-xs">UYU recibido (gestor)</p>
                <p className="text-xl font-bold font-mono text-slate-800">
                  ${fmt(target.amount_uyu)} {target.currency}
                </p>
                <p className="text-xs text-slate-400 mt-1">de {target.gestor_name}</p>
              </div>
              <div className="space-y-1">
                <Label>USDT recibidos *</Label>
                <Input type="number" step="any" min="0" value={form.usdt_received}
                       onChange={(e) => setForm({ ...form, usdt_received: e.target.value })}
                       placeholder="Ej: 25.40" data-testid="usdt-received-input" required />
              </div>
              <div className="space-y-1">
                <Label>Tasa UYU/USDT (opcional — se calcula sola)</Label>
                <Input type="number" step="any" min="0" value={form.rate_uyu_usdt}
                       onChange={(e) => setForm({ ...form, rate_uyu_usdt: e.target.value })}
                       placeholder="auto" data-testid="rate-input" />
                {form.usdt_received && target.amount_uyu && (
                  <p className="text-[11px] text-slate-400 font-mono">
                    Auto: 1 USDT = {fmt(target.amount_uyu / parseFloat(form.usdt_received || 1), 4)} UYU
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label>Nota (opcional)</Label>
                <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                       placeholder="Comentario interno" data-testid="note-input" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setTarget(null)}>Cancelar</Button>
                <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-settle-btn">
                  {saving ? 'Guardando…' : 'Registrar'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DepositUsdtSettlementsPage;
